<template>
  <div class="create-listing-root">
    <head>
      <meta charset="utf-8" />
      <title>FLOCKR | Create Listing</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="Create a new livestock listing" name="description" />
      <meta content="Flockr" name="author" />
      <link rel="shortcut icon" href="/assets/images/favicon.svg">
      <link href="/assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="/assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="/assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
      <link href="/assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="/assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="/assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <!-- div, not <body>: a nested body element is invalid DOM and breaks
         automation/assistive tooling; document.body gets data-sidebar in syncTheme() -->
    <div :data-sidebar="darkMode ? 'dark' : 'light'">
    <div id="layout-wrapper">

      <!-- ========== TOP BAR ========== -->
      <nav class="cl-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center" style="gap:16px;">
            <a href="javascript:void(0)" @click="$router.push('/')">
              <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" />
            </a>
            <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Create Listing</span>
          </div>
          <button class="btn btn-sm btn-outline-secondary" @click="cancelListing">Cancel</button>
        </div>
      </nav>

      <!-- ========== PROGRESS BAR ========== -->
      <div class="cl-progress-wrap">
        <div class="container">
          <div class="cl-progress-bar">
            <div class="cl-progress-fill" :style="'width:' + progressPercent + '%'"></div>
          </div>
          <div class="cl-steps-row">
            <div v-for="(s, si) in steps" :key="si"
                 class="cl-step" :class="{ active: step === si, done: step > si }"
                 @click="goToStep(si)">
              <div class="cl-step-num">
                <i v-if="step > si" class="mdi mdi-check"></i>
                <span v-else>{{ si + 1 }}</span>
              </div>
              <div class="cl-step-label d-none d-md-block">{{ s }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== FORM BODY ========== -->
      <div class="container cl-container">
        <div class="row justify-content-center">
          <div class="col-lg-8">

            <!-- ===== STEP 0: BASIC INFO ===== -->
            <div v-if="step === 0" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-information-outline me-2"></i>Basic Information</h5>

              <!-- Dev-Requests #4 — what are you selling? Live animals or livestock
                   products (milk, eggs, hides, honey, meat, wool). The two branches
                   have different required fields downstream. -->
              <div class="mb-3">
                <label class="cl-label">What are you selling? <span class="text-danger">*</span></label>
                <div class="d-flex gap-2 flex-wrap">
                  <button class="cl-type-btn" :class="{active: form.listingKind === 'live_animal'}" @click="form.listingKind = 'live_animal'">
                    <i class="mdi mdi-cow me-1"></i> Live Animals
                  </button>
                  <button class="cl-type-btn" :class="{active: form.listingKind === 'livestock_product'}" @click="form.listingKind = 'livestock_product'; onKindChange()">
                    <i class="mdi mdi-basket-outline me-1"></i> Livestock Products
                  </button>
                </div>
              </div>

              <div v-if="form.listingKind === 'live_animal'" class="mb-3">
                <label class="cl-label">Listing Type <span class="text-danger">*</span></label>
                <div class="d-flex gap-2 flex-wrap">
                  <button v-for="t in listingTypes" :key="t.value" class="cl-type-btn" :class="{ active: form.listingType === t.value }" @click="form.listingType = t.value">
                    <i :class="t.icon" class="me-1"></i> {{ t.label }}
                  </button>
                </div>
              </div>

              <!-- Product category picker — only for livestock_product kind -->
              <div v-if="form.listingKind === 'livestock_product'" class="mb-3">
                <label class="cl-label">Product Category <span class="text-danger">*</span></label>
                <select v-model="form.productCategoryId" class="form-select cl-select">
                  <option value="">Select category...</option>
                  <option v-for="cat in livestockProductCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
                <small v-if="isMeatCarcass" style="color:#f1b44c;font-size:11px;display:block;margin-top:4px;">
                  <i class="mdi mdi-shield-account-outline me-1"></i>Meat (Carcass) listings are restricted to Flockr-approved aggregators. Contact Flockr to apply for the aggregator role if you don't have it yet.
                </small>
              </div>

              <div v-if="form.listingKind === 'live_animal'" class="mb-3">
                <label class="cl-label">Species <span class="text-danger">*</span></label>
                <select v-model="form.speciesId" class="form-select cl-select" @change="onSpeciesChange">
                  <option value="">Select species...</option>
                  <option v-for="sp in speciesList" :key="sp.id" :value="sp.id">{{ sp.name }}</option>
                </select>
              </div>

              <div class="mb-3" v-if="form.listingKind === 'live_animal' && breedsList.length > 0">
                <label class="cl-label">Breed</label>
                <select v-model="form.breedId" class="form-select cl-select">
                  <option value="">Select breed (optional)...</option>
                  <option v-for="br in breedsList" :key="br.id" :value="br.id">{{ br.name }}</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="cl-label">Title <span class="text-danger">*</span></label>
                <input v-model="form.title" type="text" class="form-control cl-input" placeholder="e.g. Healthy Friesian Heifer - 18 months" maxlength="300" />
                <div class="cl-hint">{{ form.title.length }}/300 characters</div>
              </div>

              <div class="mb-3">
                <label class="cl-label">Description <span class="text-danger">*</span></label>
                <textarea v-model="form.description" class="form-control cl-input" rows="4" placeholder="Describe the animal(s) — health, temperament, feeding, reason for sale..."></textarea>
              </div>

              <div class="mb-3" v-if="farms.length > 0">
                <label class="cl-label">
                  Farm <span v-if="farmRequired" class="text-danger">*</span>
                  <span v-else style="color:var(--fm-s,#6B6356);font-weight:400;">(optional)</span>
                </label>
                <select v-model="form.farmId" class="form-select cl-select" @change="onFarmPicked">
                  <option value="" :disabled="farmRequired">{{ farmRequired ? 'Select farm…' : 'Select farm... (optional)' }}</option>
                  <option v-for="f in farms" :key="f.id" :value="f.id">{{ f.name }}{{ f.latitude ? ' 📍' : '' }}</option>
                </select>
                <small v-if="form.farmId" style="color:var(--fm-s,#6B6356);font-size:11px;">
                  <span v-if="pickedFarmHasLocation"><i class="mdi mdi-check-circle me-1" style="color:#50C878;"></i>Location auto-filled from farm — you can still adjust below.</span>
                  <span v-else><i class="mdi mdi-information-outline me-1"></i>This farm has no saved location. Pin one below or update the farm record on the Farm Dashboard.</span>
                </small>
                <small v-else-if="farmRequired" style="color:#FF6B6B;font-size:11px;display:block;margin-top:4px;">
                  <i class="mdi mdi-information-outline me-1"></i>As a farm staff member, your listings publish under the farm's name — please pick which farm.
                </small>
              </div>
            </div>

            <!-- ===== STEP 1: DETAILS (branches by kind) ===== -->
            <div v-if="step === 1" class="cl-card">
              <h5 class="cl-card-title">
                <i class="mdi me-2" :class="form.listingKind === 'livestock_product' ? 'mdi-basket-outline' : 'mdi-paw'"></i>
                {{ form.listingKind === 'livestock_product' ? 'Product Details' : 'Animal Details' }}
              </h5>

              <!-- ---- LIVE ANIMAL branch ---- -->
              <template v-if="form.listingKind === 'live_animal'">
                <div v-if="form.listingType === 'individual'" class="row g-3">
                  <div class="col-md-4">
                    <label class="cl-label">Sex</label>
                    <select v-model="form.sex" class="form-select cl-select">
                      <option value="">Unknown</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="cl-label">Age (months)</label>
                    <input v-model="form.ageMonths" type="number" class="form-control cl-input" min="0" placeholder="e.g. 18" />
                  </div>
                  <div class="col-md-4">
                    <label class="cl-label">Weight (kg)</label>
                    <input v-model="form.weightKg" type="number" step="0.1" class="form-control cl-input" min="0" placeholder="e.g. 350" />
                  </div>
                </div>

                <div v-else class="row g-3">
                  <div class="col-md-4">
                    <label class="cl-label">Total Quantity <span class="text-danger">*</span></label>
                    <input v-model="form.totalQuantity" type="number" class="form-control cl-input" min="1" placeholder="e.g. 20" />
                  </div>
                  <div class="col-md-4">
                    <label class="cl-label">Available Quantity</label>
                    <input v-model="form.availableQuantity" type="number" class="form-control cl-input" min="1" placeholder="e.g. 20" />
                  </div>
                  <div class="col-md-4">
                    <label class="cl-label">Min Order Quantity</label>
                    <input v-model="form.minOrderQuantity" type="number" class="form-control cl-input" min="1" placeholder="1" />
                  </div>
                  <div class="col-md-6">
                    <label class="cl-label">Average Weight (kg)</label>
                    <input v-model="form.avgWeightKg" type="number" step="0.1" class="form-control cl-input" min="0" placeholder="e.g. 35" />
                  </div>
                  <div class="col-md-6">
                    <label class="cl-label">Sex</label>
                    <select v-model="form.sex" class="form-select cl-select">
                      <option value="">Mixed / Unknown</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                </div>

                <div class="mt-3 p-3" style="background:var(--fm-bg2,#0F0F0F);border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);">
                  <div class="d-flex align-items-center gap-2 mb-2" style="color:#BFA164;font-size:13px;">
                    <i class="mdi mdi-lightbulb-outline"></i> <strong>Tip</strong>
                  </div>
                  <p style="color:var(--fm-m,#A09888);font-size:12px;margin:0;">
                    Listings with weight and age details get 2x more inquiries. Buyers value transparency!
                  </p>
                </div>
              </template>

              <!-- ---- LIVESTOCK PRODUCT branch (Dev-Requests #4) ---- -->
              <template v-else>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="cl-label">Available Quantity <span class="text-danger">*</span></label>
                    <input v-model="form.availableQuantity" type="number" class="form-control cl-input" min="1" :placeholder="productQtyPlaceholder" />
                  </div>
                  <div class="col-md-6">
                    <label class="cl-label">Minimum Order</label>
                    <input v-model="form.minOrderQuantity" type="number" class="form-control cl-input" min="1" :placeholder="productMinOrderPlaceholder" />
                  </div>
                  <div class="col-md-6">
                    <label class="cl-label">Unit of Measure</label>
                    <select v-model="form.priceUnit" class="form-select cl-select">
                      <option v-for="u in productUnitOptions" :key="u.value" :value="u.value">{{ u.label }}</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="cl-label">{{ isMeatCarcass ? 'Slaughtered on' : 'Harvested / Prepared on' }}</label>
                    <input v-model="form.productHarvestDate" type="date" class="form-control cl-input" />
                    <div class="cl-hint">{{ isMeatCarcass ? 'Buyers expect freshly-slaughtered meat — the date shows on the listing.' : "Fresh milk = today's, hides = curing date, etc." }}</div>
                  </div>
                </div>

                <!-- Butchery workflow — cuts breakdown for Meat (Carcass) listings.
                     Each cut carries kg, price/kg and the Flockr A-D grade; the
                     listing price is auto-computed as the sum of the cut totals. -->
                <div v-if="isMeatCarcass" class="mt-3">
                  <label class="cl-label">Cuts &amp; Pricing <span class="text-danger">*</span></label>
                  <div class="cl-hint mb-2">List each cut with its weight, price per kg and quality grade. Grade key: A Prime · B Choice · C Standard · D Utility.</div>
                  <div v-for="(cut, ci) in cuts" :key="ci" class="row g-2 mb-2 align-items-end">
                    <div class="col-md-4">
                      <label class="cl-label" style="font-size:11px;">Cut</label>
                      <input v-model="cut.cut_name" type="text" class="form-control cl-input" list="meat-cut-options" maxlength="80" placeholder="e.g. Front Quarter" />
                    </div>
                    <div class="col-md-2">
                      <label class="cl-label" style="font-size:11px;">Weight (kg)</label>
                      <input v-model="cut.weight_kg" type="number" step="0.1" min="0" class="form-control cl-input" placeholder="kg" />
                    </div>
                    <div class="col-md-2">
                      <label class="cl-label" style="font-size:11px;">Price/kg</label>
                      <input v-model="cut.price_per_kg" type="number" step="0.01" min="0" class="form-control cl-input" placeholder="KES" />
                    </div>
                    <div class="col-md-2">
                      <label class="cl-label" style="font-size:11px;">Grade</label>
                      <select v-model="cut.quality_grade" class="form-select cl-select">
                        <option value="">—</option>
                        <option v-for="g in gradeOptions" :key="g.value" :value="g.value">{{ g.value }} — {{ g.label }}</option>
                      </select>
                    </div>
                    <div class="col-md-2 d-flex align-items-center gap-2">
                      <span style="font-size:11px;color:#BFA164;white-space:nowrap;">{{ cutLineTotal(cut) > 0 ? 'KES ' + formatPrice(cutLineTotal(cut)) : '' }}</span>
                      <button type="button" class="btn btn-sm" style="color:#FF6B6B;border:1px solid #FF6B6B33;border-radius:6px;padding:2px 8px;" @click="cuts.splice(ci, 1)"><i class="mdi mdi-trash-can-outline"></i></button>
                    </div>
                  </div>
                  <datalist id="meat-cut-options">
                    <option v-for="c in meatCutSuggestions" :key="c" :value="c"></option>
                  </datalist>
                  <button type="button" class="cl-type-btn" @click="cuts.push({ cut_name: '', weight_kg: '', price_per_kg: '', quality_grade: '' })">
                    <i class="mdi mdi-plus me-1"></i> Add cut
                  </button>
                  <div v-if="cutsTotal > 0" class="cl-perkg-total mt-2">
                    <i class="mdi mdi-calculator me-1"></i>
                    Listing total: <strong>{{ form.currency }} {{ formatPrice(cutsTotal) }}</strong>
                    <span style="color:var(--fm-s,#6B6356);font-size:11px;">({{ cutsTotalKg }} kg across {{ validCuts.length }} cut{{ validCuts.length === 1 ? '' : 's' }} — includes your commission; used as the listing price)</span>
                  </div>
                </div>

                <div class="mt-3 p-3" style="background:var(--fm-bg2,#0F0F0F);border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);">
                  <div class="d-flex align-items-center gap-2 mb-2" style="color:#BFA164;font-size:13px;">
                    <i class="mdi mdi-lightbulb-outline"></i> <strong>Tip</strong>
                  </div>
                  <p style="color:var(--fm-m,#A09888);font-size:12px;margin:0;">
                    Perishables sell fastest when buyers can see the harvest date. Include a photo of the product so they know what to expect.
                  </p>
                </div>
              </template>
            </div>

            <!-- ===== STEP 2: PRICING ===== -->
            <div v-if="step === 2" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-cash me-2"></i>Pricing</h5>

              <div class="mb-3">
                <label class="cl-label">Price Type</label>
                <div class="d-flex gap-2 flex-wrap">
                  <button v-for="pt in priceTypes" :key="pt.value" class="cl-type-btn" :class="{ active: form.priceType === pt.value }" @click="setPriceType(pt.value)">
                    {{ pt.label }}
                  </button>
                </div>
              </div>

              <!-- Pilot-reviewer fix (issue #2): explicit price unit so buyers
                   know whether 280 means per kg live, per head, or per batch. -->
              <div class="mb-3">
                <label class="cl-label">Price Unit <span class="text-danger">*</span></label>
                <div class="d-flex gap-2 flex-wrap">
                  <button v-for="pu in activePriceUnits" :key="pu.value" type="button" class="cl-type-btn" :class="{ active: form.priceUnit === pu.value }" @click="form.priceUnit = pu.value">
                    {{ pu.label }}
                  </button>
                </div>
                <div class="cl-hint">Buyers see "KES 280 {{ priceUnitDisplay }}" on the card — pick the unit that matches your price.</div>
              </div>

              <!-- PER-KG: capture price/kg + show live total (and accept manual total override) -->
              <div v-if="showPerKgCalc" class="mb-3">
                <div class="row g-2">
                  <div class="col-md-6">
                    <label class="cl-label">Price per kg (KES) <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-weight:700;">KES/kg</span>
                      <input v-model="form.pricePerKg" type="number" step="0.01" class="form-control cl-input" min="0" placeholder="e.g. 600" style="border-left:none;" @input="recalcFromPerKg" />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="cl-label">Live Weight (kg)</label>
                    <div class="input-group">
                      <input v-model="form.weightKg" type="number" step="0.1" class="form-control cl-input" min="0" placeholder="e.g. 250" @input="recalcFromPerKg" />
                      <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-weight:700;">kg</span>
                    </div>
                    <div class="cl-hint" v-if="!form.weightKg">Live weight is required for per-kg pricing.</div>
                  </div>
                </div>
                <div class="cl-perkg-total mt-2" v-if="computedPerKgTotal > 0">
                  <i class="mdi mdi-calculator me-1"></i>
                  Total list price: <strong>{{ form.currency }} {{ formatPrice(computedPerKgTotal) }}</strong>
                  <span style="color:var(--fm-s,#6B6356);font-size:11px;">({{ form.weightKg }} kg × {{ form.currency }} {{ formatPrice(form.pricePerKg) }}/kg)</span>
                  <span v-if="perKgEdited" style="color:#f1b44c;font-size:11px;margin-left:8px;"><i class="mdi mdi-information-outline me-1"></i>You overrode the total below — that's what buyers will see.</span>
                </div>
                <div class="mt-2"><label class="cl-label">Override total (optional)</label>
                  <div class="input-group">
                    <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-weight:700;">{{ form.currency }}</span>
                    <input v-model="form.price" type="number" step="0.01" class="form-control cl-input" min="0" :placeholder="computedPerKgTotal ? String(computedPerKgTotal) : 'auto-calculated'" style="border-left:none;" @input="perKgEdited = true" />
                  </div>
                </div>
              </div>

              <!-- ALL OTHER PRICE TYPES: original simple price input -->
              <div v-else class="mb-3">
                <label class="cl-label">Currency</label>
                <select v-model="form.currency" class="form-control cl-input mb-2">
                  <option v-for="c in currencies" :key="c.code" :value="c.code">{{ c.code }} — {{ c.name }}</option>
                </select>
                <label class="cl-label">Price ({{ form.currency }}) <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-weight:700;">{{ form.currency }}</span>
                  <input v-model="form.price" type="number" step="0.01" class="form-control cl-input" min="0" placeholder="0.00" style="border-left:none;" />
                </div>
                <div class="cl-hint" v-if="form.listingType !== 'individual' && form.totalQuantity > 1">
                  Total value: {{ form.currency }} {{ formatPrice(form.price * (form.totalQuantity || 1)) }} for {{ form.totalQuantity }} animals
                </div>
              </div>

              <!-- DELIVERY OPTIONS -->
              <div class="cl-delivery-section mt-4" style="padding-top:16px;border-top:1px solid var(--fm-br);">
                <h6 style="color:var(--fm-h,#F5F0E8);font-size:14px;font-weight:600;margin-bottom:12px;"><i class="mdi mdi-truck me-2" style="color:#BFA164;"></i>Delivery Options</h6>
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;">I can deliver to buyers</div>
                    <div style="color:var(--fm-s,#6B6356);font-size:11px;">Set delivery fees by distance zone</div>
                  </div>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" v-model="form.offersDelivery" />
                  </div>
                </div>
                <div v-if="form.offersDelivery">
                  <!-- BASE FEE + PER KM -->
                  <div class="cl-zone-grid mb-3">
                    <div class="cl-zone">
                      <div class="cl-zone-header"><i class="mdi mdi-cash" style="color:#50C878;"></i><span>Base Fee (Minimum)</span></div>
                      <div class="cl-zone-desc">The least a buyer pays for any delivery</div>
                      <div class="input-group input-group-sm mt-2">
                        <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-size:11px;">KES</span>
                        <input v-model="form.deliveryBaseFee" type="number" step="100" class="form-control cl-input" min="0" placeholder="e.g. 500" style="border-left:none;font-size:13px;" />
                      </div>
                    </div>
                    <div class="cl-zone">
                      <div class="cl-zone-header"><i class="mdi mdi-map-marker-distance" style="color:#BFA164;"></i><span>Per KM Rate</span></div>
                      <div class="cl-zone-desc">Charge per kilometer of distance</div>
                      <div class="input-group input-group-sm mt-2">
                        <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-size:11px;">KES</span>
                        <input v-model="form.deliveryPerKm" type="number" step="5" class="form-control cl-input" min="0" placeholder="e.g. 50" style="border-left:none;font-size:13px;" />
                      </div>
                    </div>
                    <div class="cl-zone">
                      <div class="cl-zone-header"><i class="mdi mdi-map-marker-off" style="color:#FF8C00;"></i><span>Max Distance</span></div>
                      <div class="cl-zone-desc">Furthest you'll deliver (KM)</div>
                      <div class="input-group input-group-sm mt-2">
                        <span class="input-group-text" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:var(--fm-s,#6B6356);font-size:11px;">KM</span>
                        <input v-model="form.deliveryMaxKm" type="number" step="10" class="form-control cl-input" min="0" placeholder="e.g. 200" style="border-left:none;font-size:13px;" />
                      </div>
                    </div>
                  </div>
                  <div v-if="form.deliveryBaseFee && form.deliveryPerKm" style="background:rgba(80,200,120,0.06);border:1px solid rgba(80,200,120,0.15);border-radius:8px;padding:10px 14px;font-size:12px;color:var(--fm-m,#A09888);margin-bottom:12px;">
                    <i class="mdi mdi-calculator me-1" style="color:#50C878;"></i>
                    <strong style="color:var(--fm-h,#F5F0E8);">Example:</strong>
                    50km = KES {{ formatPrice(Math.max(Number(form.deliveryBaseFee), 50 * Number(form.deliveryPerKm))) }} ·
                    100km = KES {{ formatPrice(Math.max(Number(form.deliveryBaseFee), 100 * Number(form.deliveryPerKm))) }} ·
                    200km = KES {{ formatPrice(Math.max(Number(form.deliveryBaseFee), 200 * Number(form.deliveryPerKm))) }}
                  </div>
                  <div class="cl-hint mb-3">Buyer's fee is Distance × Per KM rate, but never less than the base fee. Leave max distance blank for unlimited.</div>
                  <div class="mb-3">
                    <label class="cl-label">Delivery Notes (optional)</label>
                    <input v-model="form.deliveryNotes" type="text" class="form-control cl-input" placeholder="e.g. Available Mon-Fri, own truck, 1-2 days" />
                  </div>
                </div>
                <div style="background:rgba(191,161,100,0.08);border:1px solid rgba(191,161,100,0.2);border-radius:8px;padding:10px 14px;font-size:11px;color:var(--fm-m,#A09888);">
                  <i class="mdi mdi-information me-1" style="color:#BFA164;"></i>
                  Buyers can always choose to collect from your location for free. Flockr Transport is also available as a third option.
                </div>
              </div>
            </div>
            <div v-if="step === 3" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-map-marker me-2"></i>Location</h5>

              <!-- County / sub-county dropdowns removed — listings are now location-pinned via GPS so the platform works worldwide.
                   For Kenyan listings, county_id is still auto-matched from the GPS pin in useMyLocation(). -->
              <div v-if="form.countyId" class="mb-3" style="background:rgba(80,200,120,.06);border:1px solid rgba(80,200,120,.3);border-radius:8px;padding:8px 12px;font-size:11px;color:#50C878;">
                <i class="mdi mdi-check-circle me-1"></i>Detected county: <strong>{{ getCountyName() }}</strong>
              </div>

              <!-- SEARCHABLE LOCATION -->
              <div class="mb-3" style="position:relative;">
                <label class="cl-label"><i class="mdi mdi-crosshairs-gps me-1" style="color:#BFA164;"></i>Pin Exact Location <span class="text-danger">*</span></label>
                <div style="display:flex;gap:8px;">
                  <div style="flex:1;position:relative;">
                    <input v-model="locQuery" type="text" class="form-control cl-input" placeholder="Search town, market, area..." @input="onListingLocSearch" @focus="showListingLocResults = true" autocomplete="off" style="padding-right:36px;" />
                    <i v-if="geoLoading" class="mdi mdi-loading mdi-spin" style="position:absolute;right:12px;top:50%;transform:translateY(-50%);color:var(--fm-s,#6B6356);"></i>
                    <i v-else-if="form.latitude" class="mdi mdi-check-circle" style="position:absolute;right:12px;top:50%;transform:translateY(-50%);color:#50C878;"></i>
                  </div>
                  <button class="cl-gps-btn" @click="useMyLocation" :disabled="geoLoading" title="Use my current location">
                    <i v-if="geoLoading" class="mdi mdi-loading mdi-spin"></i>
                    <i v-else class="mdi mdi-crosshairs-gps"></i>
                  </button>
                </div>
                <div v-if="showListingLocResults && listingLocResults.length > 0" class="cl-loc-dropdown">
                  <div v-for="(loc, li) in listingLocResults" :key="li" class="cl-loc-item" @click="pickListingLocation(loc)">
                    <i class="mdi mdi-map-marker" style="color:#BFA164;flex-shrink:0;margin-top:2px;"></i>
                    <div><div style="font-size:13px;color:var(--fm-h,#F5F0E8);">{{ loc.display_name }}</div><div style="font-size:11px;color:var(--fm-s,#6B6356);">{{ loc.area }}</div></div>
                  </div>
                </div>
                <div style="font-size:11px;color:var(--fm-s,#6B6356);margin-top:6px;line-height:1.5;">
                  <i class="mdi mdi-lightbulb-on-outline me-1" style="color:#BFA164;"></i>
                  Can't find a specific building or office park? Search the road or nearest landmark, then tap or drag the pin on the map below to the exact spot.
                </div>
              </div>

              <!-- INTERACTIVE MAP -->
              <div class="mb-3">
                <div class="cl-map-container">
                  <div id="listingMap" class="cl-interactive-map"></div>
                  <div class="cl-map-hint"><i class="mdi mdi-gesture-tap me-1"></i>Tap the map to pin animal location</div>
                </div>
                <div v-if="form.latitude && form.longitude" style="font-size:11px;color:#50C878;margin-top:6px;">
                  <i class="mdi mdi-check-circle me-1"></i>{{ form.locationDescription || 'Location set' }}
                  <span style="color:var(--fm-s,#6B6356);">({{ Number(form.latitude).toFixed(4) }}, {{ Number(form.longitude).toFixed(4) }})</span>
                </div>
              </div>

              <div class="mb-3">
                <label class="cl-label">Location Description (optional)</label>
                <input v-model="form.locationDescription" type="text" class="form-control cl-input" placeholder="e.g. Near Meru town, along the Nanyuki highway" />
              </div>
            </div>

            <!-- ===== STEP 4: PHOTOS ===== -->
            <div v-if="step === 4" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-camera me-2"></i>Photos</h5>
              <p style="color:var(--fm-m,#A09888);font-size:13px;">Upload up to 6 photos. First photo becomes the primary image. Max 2MB each.</p>

              <div class="cl-photo-grid">
                <div v-for="(photo, pi) in photos" :key="'p-'+pi" class="cl-photo-slot">
                  <img :src="photo.preview" :alt="'Photo ' + (pi+1)" />
                  <button class="cl-photo-remove" @click="removePhoto(pi)"><i class="mdi mdi-close"></i></button>
                  <span v-if="pi === 0" class="cl-photo-primary">Primary</span>
                  <span v-if="photo.file" class="cl-photo-size">{{ formatFileSize(photo.file.size) }}</span>
                  <span v-else-if="photo.fromAnimal" class="cl-photo-size" style="background:rgba(80,200,120,.8);">From farm</span>
                </div>
                <label v-if="photos.length < 6 && !compressing" class="cl-photo-add">
                  <input type="file" accept="image/*" multiple @change="handlePhotoUpload" style="display:none;" />
                  <i class="mdi mdi-camera-plus" style="font-size:28px;color:#BFA164;"></i>
                  <span style="font-size:12px;color:var(--fm-s,#6B6356);">Add Photo</span>
                </label>
                <div v-if="compressing" class="cl-photo-add" style="pointer-events:none;">
                  <div class="spinner-border spinner-border-sm text-warning"></div>
                  <span style="font-size:11px;color:#BFA164;">Compressing...</span>
                </div>
              </div>

              <div class="mt-3 p-3" style="background:var(--fm-bg2,#0F0F0F);border-radius:8px;border:1px solid var(--fm-br,#1E1E1E);">
                <div class="d-flex align-items-center gap-2 mb-2" style="color:#BFA164;font-size:13px;">
                  <i class="mdi mdi-lightbulb-outline"></i> <strong>Photo Tips</strong>
                </div>
                <p style="color:var(--fm-m,#A09888);font-size:12px;margin:0;">
                  Take clear, well-lit photos from multiple angles. Show the full body, close-up of the face, and any distinctive markings. Listings with 3+ photos sell 4x faster. Photos are auto-compressed to save data.
                </p>
              </div>
            </div>

            <!-- ===== STEP 5: REVIEW ===== -->
            <div v-if="step === 5" class="cl-card">
              <h5 class="cl-card-title"><i class="mdi mdi-check-circle-outline me-2"></i>Review &amp; Publish</h5>

              <div class="cl-review-section">
                <div class="cl-review-label">Kind</div>
                <div class="cl-review-value">{{ form.listingKind === 'livestock_product' ? '🥛 Livestock Product' : '🐄 Live Animal (' + form.listingType + ')' }}</div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Title</div>
                <div class="cl-review-value">{{ form.title }}</div>
              </div>
              <div class="cl-review-section" v-if="form.listingKind === 'livestock_product'">
                <div class="cl-review-label">Product Category</div>
                <div class="cl-review-value">{{ selectedProductCategoryName || '—' }}</div>
              </div>
              <div class="cl-review-section" v-else>
                <div class="cl-review-label">Species / Breed</div>
                <div class="cl-review-value">{{ getSpeciesName() }} <span v-if="getBreedName()">/ {{ getBreedName() }}</span></div>
              </div>
              <div class="cl-review-section" v-if="form.listingKind === 'live_animal' && form.sex">
                <div class="cl-review-label">Sex</div>
                <div class="cl-review-value">{{ form.sex }}</div>
              </div>
              <div class="cl-review-section" v-if="form.listingKind === 'live_animal' && form.ageMonths">
                <div class="cl-review-label">Age</div>
                <div class="cl-review-value">{{ form.ageMonths }} months</div>
              </div>
              <div class="cl-review-section" v-if="form.listingKind === 'live_animal' && form.weightKg">
                <div class="cl-review-label">Weight</div>
                <div class="cl-review-value">{{ form.weightKg }} kg</div>
              </div>
              <div class="cl-review-section" v-if="form.availableQuantity || form.totalQuantity">
                <div class="cl-review-label">{{ form.listingKind === 'livestock_product' ? 'Available quantity' : 'Quantity' }}</div>
                <div class="cl-review-value">{{ form.availableQuantity || form.totalQuantity }} <span style="color:var(--fm-s,#6B6356);font-weight:400;">{{ priceUnitDisplay.replace('/','').trim() }}</span></div>
              </div>
              <div class="cl-review-section" v-if="form.listingKind === 'livestock_product' && form.productHarvestDate">
                <div class="cl-review-label">{{ isMeatCarcass ? 'Slaughtered on' : 'Harvested / Prepared' }}</div>
                <div class="cl-review-value">{{ form.productHarvestDate }}</div>
              </div>
              <div class="cl-review-section" v-if="isMeatCarcass && validCuts.length > 0">
                <div class="cl-review-label">Cuts ({{ cutsTotalKg }} kg total)</div>
                <div class="cl-review-value" style="font-size:13px;">
                  <div v-for="(cut, rci) in validCuts" :key="'rc-'+rci" class="d-flex justify-content-between" style="padding:2px 0;">
                    <span>{{ cut.cut_name }}<span v-if="cut.quality_grade" style="color:#BFA164;"> · Grade {{ cut.quality_grade }}</span></span>
                    <span>{{ cut.weight_kg }} kg × {{ formatPrice(cut.price_per_kg) }}/kg = <strong>{{ formatPrice(cutLineTotal(cut)) }}</strong></span>
                  </div>
                </div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Price</div>
                <div class="cl-review-value" style="color:#BFA164;font-weight:700;">{{ form.currency }} {{ formatPrice(form.price) }} <span style="font-weight:400;color:var(--fm-s,#6B6356);">({{ form.priceType }})</span></div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Delivery</div>
                <div class="cl-review-value" v-if="form.offersDelivery">Seller delivers — Base: KES {{ formatPrice(form.deliveryBaseFee || 0) }} + KES {{ formatPrice(form.deliveryPerKm || 0) }}/km<span v-if="form.deliveryMaxKm"> · Max {{ form.deliveryMaxKm }}km</span><span v-if="form.deliveryNotes" style="color:var(--fm-s,#6B6356);"> · {{ form.deliveryNotes }}</span></div>
                <div class="cl-review-value" v-else style="color:var(--fm-s,#6B6356);">Buyer collects only</div>
              </div>
              <div class="cl-review-section">
                <div class="cl-review-label">Location</div>
                <div class="cl-review-value">{{ getCountyName() }} <span v-if="form.locationDescription" style="color:var(--fm-s,#6B6356);">— {{ form.locationDescription }}</span></div>
                <div v-if="form.latitude" style="font-size:11px;color:#50C878;margin-top:2px;"><i class="mdi mdi-map-marker-check me-1"></i>Coordinates pinned</div>
              </div>
              <div class="cl-review-section" v-if="photos.length > 0">
                <div class="cl-review-label">Photos</div>
                <div class="d-flex gap-2 flex-wrap">
                  <img v-for="(ph, phi) in photos" :key="'rp-'+phi" :src="ph.preview" style="width:60px;height:60px;object-fit:cover;border-radius:6px;border:1px solid var(--fm-br,#2A2520);" />
                </div>
              </div>

              <div v-if="submitError" class="alert alert-danger mt-3" style="background:#2D1111;border-color:#5C2020;color:#FF6B6B;font-size:13px;">
                <i class="mdi mdi-alert-circle me-1"></i> {{ submitError }}
              </div>
            </div>

            <!-- ===== NAVIGATION BUTTONS ===== -->
            <div class="cl-nav-buttons">
              <button v-if="step > 0" class="btn btn-outline-secondary" @click="prevStep">
                <i class="mdi mdi-arrow-left me-1"></i> Back
              </button>
              <div class="flex-fill"></div>
              <button v-if="step < 5" class="btn btn-warning" @click="nextStep" :disabled="!canAdvance">
                Next <i class="mdi mdi-arrow-right ms-1"></i>
              </button>
              <button v-else class="btn btn-warning" @click="submitListing" :disabled="submitting">
                <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span> Publishing...</span>
                <span v-else><i class="mdi mdi-publish me-1"></i> Publish Listing</span>
              </button>
            </div>

          </div>
        </div>
      </div>

    </div>
    </div>
  </div>
</template>

<!-- ========== STYLES ========== -->
<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style>
html[data-theme="light"] body {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}

html[data-theme="dark"] body {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}

html[data-theme="light"] .create-listing-root,
html[data-theme="light"] #layout-wrapper,
html[data-theme="light"] .cl-progress-wrap,
html[data-theme="light"] .cl-container {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}

html[data-theme="light"] .cl-topbar {
  background:#FFFFFF!important;
  border-bottom-color:#E8E2DA!important;
}

html[data-theme="dark"] .create-listing-root,
html[data-theme="dark"] #layout-wrapper,
html[data-theme="dark"] .cl-progress-wrap,
html[data-theme="dark"] .cl-container {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}

html[data-theme="dark"] .cl-topbar {
  background:rgba(10,10,10,0.95)!important;
}
</style>

<style scoped>
.create-listing-root { background:var(--fm-bg2,#0F0F0F)!important; min-height:100vh; color:var(--fm-h,#F5F0E8)!important; }

.cl-topbar {
  position:sticky; top:0; z-index:100; padding:12px 0;
  background:var(--fm-bg1,#141414)!important; backdrop-filter:blur(10px);
  border-bottom:1px solid var(--fm-br,#1E1E1E);
}

/* Progress */
.cl-progress-wrap { background:var(--fm-bg2,#0F0F0F)!important; border-bottom:1px solid var(--fm-br,#1E1E1E); padding:16px 0 12px; position:sticky; top:54px; z-index:99; }
.cl-progress-bar { height:4px; background:var(--fm-bg3,#1E1E1E)!important; border-radius:2px; overflow:hidden; margin-bottom:12px; }
.cl-progress-fill { height:100%; background:linear-gradient(90deg, #BFA164, #f1b44c); border-radius:2px; transition:width 0.3s ease; }
.cl-steps-row { display:flex; justify-content:space-between; }
.cl-step { display:flex; align-items:center; gap:6px; cursor:pointer; }
.cl-step-num {
  width:26px; height:26px; border-radius:50%; border:2px solid #2A2520;
  display:flex; align-items:center; justify-content:center;
  font-size:12px; font-weight:700; color:var(--fm-s,#6B6356)!important; background:var(--fm-bg1,#141414)!important;
  transition:all 0.2s ease;
}
.cl-step.active .cl-step-num { border-color:#BFA164; color:#BFA164!important; background:rgba(191,161,100,0.1)!important; }
.cl-step.done .cl-step-num { border-color:#50C878; color:#50C878!important; background:rgba(80,200,120,0.1)!important; }
.cl-step-label { font-size:11px; color:var(--fm-s,#6B6356)!important; }
.cl-step.active .cl-step-label { color:#BFA164!important; font-weight:600; }
.cl-step.done .cl-step-label { color:#50C878!important; }

/* Container */
.cl-container { padding-top:24px; padding-bottom:100px; }

/* Client issue 13 — default Bootstrap switch was a white pill on a white card
   in light theme. Explicit track/knob colors keep it visible in both themes. */
.cl-delivery-section .form-check-input {
  width:44px; height:24px; cursor:pointer;
  background-color:#9B9186!important;
  border:1px solid #6B6356!important;
  background-image:url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
}
.cl-delivery-section .form-check-input:focus {
  border-color:#BFA164; box-shadow:0 0 0 3px rgba(191,161,100,0.25);
  background-image:url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
}
.cl-delivery-section .form-check-input:checked {
  background-color:#BFA164!important; border-color:#BFA164!important;
}

/* Cards */
.cl-card { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#1E1E1E); border-radius:12px; padding:24px; margin-bottom:20px; }
.cl-card-title { font-size:16px; font-weight:700; color:var(--fm-h,#F5F0E8)!important; margin-bottom:20px; }

/* Form Elements */
.cl-label { font-size:13px; font-weight:600; color:#D4C9B8!important; margin-bottom:6px; display:block; }
.cl-hint { font-size:11px; color:var(--fm-s,#6B6356)!important; margin-top:4px; }
.cl-zone-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; }
.cl-zone { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#1E1E1E); border-radius:10px; padding:12px; }
.cl-zone-header { display:flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:var(--fm-h,#F5F0E8); margin-bottom:2px; }
.cl-zone-desc { font-size:10px; color:var(--fm-s,#6B6356); }
.cl-map-container { position:relative; border-radius:10px; overflow:hidden; border:1px solid var(--fm-br,#2A2520); }
.cl-interactive-map { width:100%; height:250px; background:var(--fm-bg2,#0F0F0F); z-index:1; }
.cl-map-hint { position:absolute; bottom:8px; left:50%; transform:translateX(-50%); background:rgba(10,10,10,0.85); border:1px solid var(--fm-br,#2A2520); border-radius:20px; padding:4px 14px; font-size:11px; color:var(--fm-m,#A09888); z-index:10; pointer-events:none; white-space:nowrap; }
.cl-gps-btn { background:var(--fm-bg3,#1E1E1E); border:1px solid var(--fm-br,#2A2520); color:#BFA164; width:44px; min-width:44px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:18px; transition:all .2s; cursor:pointer; }
.cl-gps-btn:hover { background:#2A2520; border-color:#BFA164; }
.cl-gps-btn:disabled { opacity:0.5; cursor:not-allowed; }
.cl-loc-dropdown { position:absolute; top:100%; left:0; right:0; z-index:50; background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#2A2520); border-radius:8px; margin-top:4px; max-height:220px; overflow-y:auto; box-shadow:0 8px 30px rgba(0,0,0,0.5); }
.cl-loc-item { display:flex; align-items:flex-start; gap:8px; padding:10px 14px; cursor:pointer; transition:background .15s; }
.cl-loc-item:hover { background:rgba(191,161,100,0.08); }
.cl-loc-item:not(:last-child) { border-bottom:1px solid var(--fm-br,#1E1E1E); }
.cl-input {
  background:var(--fm-bg2,#0F0F0F)!important; border:1px solid var(--fm-br,#2A2520); color:var(--fm-h,#F5F0E8)!important; border-radius:8px;
}
.cl-input:focus { border-color:#BFA164; background:var(--fm-bg2,#0F0F0F)!important; color:var(--fm-h,#F5F0E8)!important; box-shadow:0 0 0 2px rgba(191,161,100,0.15); }
.cl-input::placeholder { color:#4A4540!important; }
.cl-select { background:var(--fm-bg2,#0F0F0F)!important; border:1px solid var(--fm-br,#2A2520); color:var(--fm-h,#F5F0E8)!important; border-radius:8px; }
.cl-select:focus { border-color:#BFA164; box-shadow:0 0 0 2px rgba(191,161,100,0.15); }
.cl-select option { background:var(--fm-bg1,#141414)!important; color:var(--fm-h,#F5F0E8)!important; }

/* Type Buttons */
.cl-type-btn {
  padding:8px 16px; border-radius:8px; border:1px solid var(--fm-br,#2A2520);
  background:var(--fm-bg2,#0F0F0F)!important; color:var(--fm-m,#A09888)!important; font-size:13px; cursor:pointer;
  transition:all 0.2s ease;
}
.cl-type-btn:hover { border-color:#BFA164; color:var(--fm-h,#F5F0E8)!important; }
.cl-type-btn.active { background:rgba(191,161,100,0.15)!important; border-color:#BFA164; color:#BFA164!important; font-weight:600; }

/* Photo Grid */
.cl-photo-grid { display:flex; flex-wrap:wrap; gap:12px; }
.cl-photo-slot {
  position:relative; width:110px; height:110px; border-radius:10px;
  overflow:hidden; border:1px solid var(--fm-br,#2A2520);
}
.cl-photo-slot img { width:100%; height:100%; object-fit:cover; }
.cl-photo-remove {
  position:absolute; top:4px; right:4px; width:22px; height:22px;
  border-radius:50%; background:rgba(0,0,0,0.7)!important; border:none;
  color:#fff!important; font-size:12px; cursor:pointer; display:flex;
  align-items:center; justify-content:center;
}
.cl-photo-size {
  position:absolute; bottom:4px; left:4px; background:rgba(0,0,0,0.7);
  color:#50C878; font-size:9px; font-weight:600; padding:1px 5px; border-radius:4px;
}
.cl-photo-primary {
  position:absolute; bottom:0; left:0; right:0; text-align:center;
  background:rgba(191,161,100,0.85)!important; color:#0A0A0A!important; font-size:10px;
  font-weight:700; padding:2px 0;
}
.cl-photo-add {
  width:110px; height:110px; border-radius:10px; border:2px dashed #2A2520;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  cursor:pointer; transition:all 0.2s ease; gap:4px;
}
.cl-photo-add:hover { border-color:#BFA164; }

/* Review */
.cl-review-section {
  display:flex; justify-content:space-between; align-items:flex-start;
  padding:10px 0; border-bottom:1px solid var(--fm-br,#1A1A1A);
}
.cl-review-section:last-of-type { border-bottom:none; }
.cl-review-label { font-size:13px; color:var(--fm-s,#6B6356)!important; min-width:120px; }
.cl-review-value { font-size:13px; color:var(--fm-h,#F5F0E8)!important; text-align:right; flex:1; }

/* Nav Buttons */
.cl-nav-buttons { display:flex; gap:12px; padding:16px 0; }

@media (max-width: 768px) {
  .cl-container { padding-top:16px; }
  .cl-card { padding:16px; }
  .cl-photo-slot, .cl-photo-add { width:90px; height:90px; }
  .cl-review-section { flex-direction:column; gap:4px; }
  .cl-review-value { text-align:left; }
  .cl-zone-grid { grid-template-columns:1fr; }
}
</style>

<!-- ========== SCRIPT ========== -->
<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";

export default {
  name: "CreateListingPage",
  data: function() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      themeObserver: null,

      step: 0,
      steps: ['Basic Info', 'Details', 'Pricing', 'Location', 'Photos', 'Review'],
      submitting: false,
      submitError: '',
      perKgEdited: false,
      form: {
        // Dev-Requests #4 — live animal vs livestock product (milk/eggs/hides/etc.)
        listingKind: 'live_animal',
        productCategoryId: '',
        productHarvestDate: '',
        listingType: 'individual',
        speciesId: '',
        breedId: '',
        title: '',
        description: '',
        farmId: '',
        animalId: '',
        batchId: '',
        sex: '',
        ageMonths: '',
        weightKg: '',
        totalQuantity: '',
        availableQuantity: '',
        minOrderQuantity: '1',
        avgWeightKg: '',
        price: '',
        pricePerKg: '',
        priceType: 'negotiable',
        // Pilot-reviewer fix (issue #2): explicit unit for the price.
        priceUnit: 'per_head',
        // Multi-currency support — defaults to seller's wallet currency (loaded onMounted),
        // overridable from the picker. Backend validates against the currencies catalogue.
        currency: 'KES',
        offersDelivery: false,
        deliveryBaseFee: '',
        deliveryPerKm: '',
        deliveryMaxKm: '',
        deliveryNotes: '',
        countyId: '',
        subCountyId: '',
        latitude: '',
        longitude: '',
        locationDescription: ''
      },
      // Butchery workflow — cuts rows for Meat (Carcass) listings.
      cuts: [],
      meatCutSuggestions: ['Front Quarter', 'Hind Quarter', 'Chapps', 'Matumbo', 'Liver', 'Ribs', 'Fillet', 'Head', 'Legs', 'Hide'],
      gradeOptions: [
        { value: 'A', label: 'Prime' },
        { value: 'B', label: 'Choice' },
        { value: 'C', label: 'Standard' },
        { value: 'D', label: 'Utility' }
      ],
      photos: [],
      compressing: false,
      locQuery: '', showListingLocResults: false, listingLocResults: [], locSearchTimer: null, geoLoading: false,
      listingMapInstance: null, listingMarker: null,
      speciesList: [],
      livestockProductCategories: [],
      breedsList: [],
      farms: [],
      counties: [],
      subCounties: [],
      currencies: [],   // populated from getCurrencies on mount
      listingTypes: [
        { value: 'individual', label: 'Single Animal', icon: 'mdi mdi-paw' },
        { value: 'batch', label: 'Batch / Herd', icon: 'mdi mdi-view-grid-outline' }
      ],
      priceTypes: [
        { value: 'fixed', label: 'Fixed Price' },
        { value: 'negotiable', label: 'Negotiable' },
        { value: 'auction', label: 'Auction' }
      ],
      // Pilot-reviewer fix (issue #2): persisted on the listing row.
      priceUnits: [
        { value: 'per_head',       label: 'Per head' },
        { value: 'per_kg_live',    label: 'Per kg (live weight)' },
        { value: 'per_kg_dressed', label: 'Per kg (dressed)' },
        { value: 'per_batch',      label: 'Per batch / lot' },
        { value: 'per_liter',      label: 'Per litre' }
      ]
    };
  },
  computed: {
    // Dev-Requests #4 — context-aware placeholders when the seller is posting a
    // livestock product (milk/eggs/hides). Nudges them toward realistic quantities.
    selectedProductCategoryName() {
      if (this.form.listingKind !== 'livestock_product' || !this.form.productCategoryId) return '';
      const cat = (this.livestockProductCategories || []).find(c => String(c.id) === String(this.form.productCategoryId));
      return cat ? cat.name : '';
    },
    // Butchery workflow — category 105 = "Meat (Carcass)", the aggregator-only
    // freshly-slaughtered flow with a per-cut price breakdown.
    isMeatCarcass() {
      return this.form.listingKind === 'livestock_product' && Number(this.form.productCategoryId) === 105;
    },
    validCuts() {
      return (this.cuts || []).filter(function(c) {
        return c.cut_name && String(c.cut_name).trim() && Number(c.weight_kg) > 0 && Number(c.price_per_kg) > 0;
      });
    },
    cutsTotal() {
      return this.validCuts.reduce(function(s, c) { return s + Number(c.weight_kg) * Number(c.price_per_kg); }, 0);
    },
    cutsTotalKg() {
      return Math.round(this.validCuts.reduce(function(s, c) { return s + Number(c.weight_kg); }, 0) * 10) / 10;
    },
    productQtyPlaceholder() {
      const n = (this.selectedProductCategoryName || '').toLowerCase();
      if (n.includes('milk (fresh)')) return 'e.g. 50 (litres)';
      if (n.includes('milk powder')) return 'e.g. 30 (kg)';
      if (n.includes('egg')) return 'e.g. 200 (pieces)';
      if (n.includes('hide') || n.includes('skin')) return 'e.g. 25 (pieces)';
      if (n.includes('meat')) return 'e.g. 100 (kg)';
      if (n.includes('honey')) return 'e.g. 40 (kg)';
      if (n.includes('wool')) return 'e.g. 15 (kg)';
      if (n.includes('manure')) return 'e.g. 10 (bags)';
      return 'e.g. 100';
    },
    // Client issues 10-11 — unit-of-measure options follow the product category
    // (eggs sell per piece/tray/dozen, milk per litre, honey per kg/jar, ...).
    productUnitOptions() {
      const n = (this.selectedProductCategoryName || '').toLowerCase();
      if (n.includes('milk (fresh)')) return [
        { value: 'per_litre', label: 'per litre' },
        { value: 'per_batch', label: 'per batch / lot' }
      ];
      if (n.includes('milk powder')) return [
        { value: 'per_kg', label: 'per kg' },
        { value: 'per_batch', label: 'per batch / lot' }
      ];
      if (n.includes('egg')) return [
        { value: 'per_piece', label: 'per piece (egg)' },
        { value: 'per_tray', label: 'per tray (30 eggs)' },
        { value: 'per_dozen', label: 'per dozen' }
      ];
      if (n.includes('hide') || n.includes('skin')) return [
        { value: 'per_piece', label: 'per piece' },
        { value: 'per_batch', label: 'per batch / lot' }
      ];
      if (n.includes('meat')) return [
        { value: 'per_kg', label: 'per kg' }
      ];
      if (n.includes('honey')) return [
        { value: 'per_kg', label: 'per kg' },
        { value: 'per_jar', label: 'per jar' }
      ];
      if (n.includes('wool')) return [
        { value: 'per_kg', label: 'per kg' },
        { value: 'per_bag', label: 'per bag' }
      ];
      if (n.includes('manure')) return [
        { value: 'per_bag', label: 'per bag' },
        { value: 'per_tonne', label: 'per tonne' }
      ];
      return [
        { value: 'per_piece', label: 'per piece' },
        { value: 'per_kg', label: 'per kg' },
        { value: 'per_litre', label: 'per litre' },
        { value: 'per_batch', label: 'per batch / lot' }
      ];
    },
    // Pricing step shows product units for products, animal units for animals.
    activePriceUnits() {
      return this.form.listingKind === 'livestock_product' ? this.productUnitOptions : this.priceUnits;
    },
    // Issue 12 — per-kg calculator keys off the price UNIT now that "Per Kg"
    // is no longer a price type. Products priced per kg just type a unit price.
    showPerKgCalc() {
      return this.form.listingKind !== 'livestock_product'
        && (this.form.priceUnit === 'per_kg_live' || this.form.priceUnit === 'per_kg_dressed');
    },
    productMinOrderPlaceholder() {
      const n = (this.selectedProductCategoryName || '').toLowerCase();
      if (n.includes('milk (fresh)')) return '5';
      if (n.includes('egg')) return '30';
      return '1';
    },
    // Pilot-reviewer fix (issue #2): preview the unit string shown on cards.
    priceUnitDisplay() {
      const u = this.form.priceUnit;
      switch (u) {
        case 'per_kg_live':    return '/ kg live';
        case 'per_kg_dressed': return '/ kg dressed';
        case 'per_batch':      return '/ batch';
        case 'per_liter':
        case 'per_litre':      return '/ litre';
        case 'per_head':       return '/ head';
        default:               return u ? '/ ' + String(u).replace(/^per_/, '').replace(/_/g, ' ') : '/ head';
      }
    },
    computedPerKgTotal() {
      var w = Number(this.form.weightKg) || 0;
      var p = Number(this.form.pricePerKg) || 0;
      return (w > 0 && p > 0) ? Math.round(w * p) : 0;
    },
    // Worker = farm staff who isn't the owner of any farm. Workers MUST list under one of
    // their farms so the listing brands under the farm name, not the individual.
    isWorkerOnly() {
      if (!this.farms || this.farms.length === 0) return false;
      return this.farms.every(function(f) { return (f.my_role || '') !== 'owner'; });
    },
    farmRequired() { return this.isWorkerOnly; },
    pickedFarmHasLocation() {
      if (!this.form.farmId) return false;
      var self = this;
      var farm = (this.farms || []).find(function(f) { return String(f.id) === String(self.form.farmId); });
      return !!(farm && farm.latitude && farm.longitude);
    },
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    progressPercent: function() {
      return Math.round(((this.step + 1) / this.steps.length) * 100);
    },
    canAdvance: function() {
      if (this.step === 0) {
        // Dev-Requests #4 — live_animal requires speciesId; livestock_product requires productCategoryId
        var kindOk = this.form.listingKind === 'livestock_product'
          ? !!this.form.productCategoryId
          : !!this.form.speciesId;
        return kindOk && this.form.title.trim().length >= 5;
      }
      if (this.step === 1) return this.isMeatCarcass ? this.validCuts.length > 0 : true;
      if (this.step === 2) return this.form.price > 0;
      if (this.step === 3) return !!this.form.latitude && !!this.form.longitude;
      if (this.step === 4) return true;
      return true;
    }
  },
  watch: {
    step: function(val) {
      if (val === 3) { var self = this; this.$nextTick(function() { self.initListingMap(); }); }
    },
    // Issues 10-11 — picking a product category auto-fills the unit of measure
    // (which is the same field the Pricing step reads).
    'form.productCategoryId': function() {
      if (this.form.listingKind !== 'livestock_product') return;
      var opts = this.productUnitOptions;
      var self = this;
      if (!opts.some(function(o) { return o.value === self.form.priceUnit; })) {
        this.form.priceUnit = opts.length ? opts[0].value : 'per_piece';
      }
      // Butchery workflow — seed the first cuts row when Meat (Carcass) is picked.
      if (this.isMeatCarcass && this.cuts.length === 0) {
        this.cuts.push({ cut_name: '', weight_kg: '', price_per_kg: '', quality_grade: '' });
      }
    },
    // Butchery workflow — listing price is the sum of the cut totals (kg × price/kg).
    cutsTotal: function(v) {
      if (this.isMeatCarcass && v > 0) this.form.price = Math.round(v * 100) / 100;
    },
    // Seed pricePerKg when a live animal switches to per-kg pricing.
    'form.priceUnit': function(v) {
      if (v === 'per_kg_live' || v === 'per_kg_dressed') {
        this.perKgEdited = false;
        var w = Number(this.form.weightKg) || 0;
        var p = Number(this.form.price) || 0;
        if (!this.form.pricePerKg && w > 0 && p > 0) {
          this.form.pricePerKg = Math.round((p / w) * 100) / 100;
        }
      }
    }
  },
  mounted: function() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    var userId = sessionStorage.getItem('userId');
    if (!userId) { this.$router.push('/login'); return; }
    this.fetchSpecies();
    this.fetchCounties();
    this.fetchCurrencies();
    this.detectDefaultCurrency();
    this.fetchFarms();

    // Pre-fill from farm animal if animalId passed
    var animalId = this.$route.query.animalId;
    if (animalId) {
      this.prefillFromAnimal(animalId);
    }

    // Pre-fill from batch if batchId passed
    var batchId = this.$route.query.batchId;
    var farmId = this.$route.query.farmId;
    if (batchId) {
      this.prefillFromBatch(batchId, farmId);
    }
  },
  beforeUnmount: function() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    if (this.locSearchTimer) {
      clearTimeout(this.locSearchTimer);
    }
  },
  methods: {
    setPriceType: function(v) {
      this.form.priceType = v;
    },
    recalcFromPerKg: function() {
      // Auto-update headline price unless the seller has explicitly overridden it.
      if (this.perKgEdited) return;
      var t = this.computedPerKgTotal;
      if (t > 0) this.form.price = t;
    },
    syncTheme: function() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';

      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    // LOCATION SEARCH (Nominatim)
    onListingLocSearch: function() {
      if (this.locSearchTimer) clearTimeout(this.locSearchTimer);
      var self = this; var q = (this.locQuery || '').trim();
      if (q.length < 3) { this.listingLocResults = []; this.showListingLocResults = false; return; }
      this.locSearchTimer = setTimeout(function() { self.doListingLocSearch(q); }, 400);
    },
    doListingLocSearch: async function(q) {
      try {
        // Bias to East Africa (matches the convention used everywhere else in the
        // portal). Without this filter Nominatim returns prominent US/EU hits for
        // ambiguous queries like "Delta Riverside" and Kenyan results never rank.
        var url = 'https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke&limit=6&q=' + encodeURIComponent(q);
        var res = await fetch(url, { headers: { 'Accept-Language': 'en' } }); var data = await res.json();
        this.listingLocResults = data.map(function(d) {
          var parts = (d.display_name || '').split(',');
          return { name: parts[0], display_name: parts.slice(0, 3).join(', ').trim(), area: parts.slice(1, 4).join(', ').trim(), lat: d.lat, lon: d.lon };
        });
        this.showListingLocResults = true;
      } catch(e) { this.listingLocResults = []; }
    },
    pickListingLocation: function(loc) {
      this.form.latitude = loc.lat;
      this.form.longitude = loc.lon;
      this.form.locationDescription = loc.display_name || loc.name;
      this.locQuery = loc.display_name || loc.name;
      this.showListingLocResults = false;
      this.updateListingPin(Number(loc.lat), Number(loc.lon));
    },
    useMyLocation: function() {
      if (!navigator.geolocation) return;
      this.geoLoading = true; var self = this;
      navigator.geolocation.getCurrentPosition(
          function(pos) {
            var lat = pos.coords.latitude; var lon = pos.coords.longitude;
            self.form.latitude = lat;
            self.form.longitude = lon;
            self.geoLoading = false;
            self.updateListingPin(lat, lon);
            self.reverseGeocode(lat, lon);
          },
          function() { self.geoLoading = false; },
          { enableHighAccuracy: true, timeout: 10000 }
      );
    },
    reverseGeocode: function(lat, lon) {
      var self = this;
      fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat=' + lat + '&lon=' + lon, { headers: { 'Accept-Language': 'en' } })
          .then(function(r) { return r.json(); })
          .then(function(d) {
            if (d && d.display_name) {
              var parts = d.display_name.split(',');
              self.form.locationDescription = parts.slice(0, 3).join(',').trim();
              self.locQuery = self.form.locationDescription;
            }
            // Auto-match Kenyan county_id silently if the pin lands in Kenya
            if (d && d.address && d.address.country_code === 'ke' && self.counties && self.counties.length) {
              var name = (d.address.county || d.address.state || '').replace(/County$/i, '').trim().toLowerCase();
              if (name) {
                var match = self.counties.find(function(c){ return (c.name||'').toLowerCase() === name; });
                if (match) {
                  self.form.countyId = match.id;
                  // Also try to populate sub-county
                  self.loadSubCounties && self.loadSubCounties();
                }
              }
            } else {
              // Outside Kenya — clear any stale county_id so we don't tag a Mombasa goat as Nairobi
              self.form.countyId = '';
              self.form.subCountyId = '';
            }
          }).catch(function() {});
    },
    loadLeaflet: function(cb) {
      if (window.L) { cb(); return; }
      var css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(css);
      var js = document.createElement('script');
      js.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      js.onload = cb;
      document.head.appendChild(js);
    },
    initListingMap: function() {
      var self = this;
      this.loadLeaflet(function() {
        self.$nextTick(function() {
          var el = document.getElementById('listingMap');
          if (!el) return;
          if (self.listingMapInstance) { self.listingMapInstance.invalidateSize(); return; }

          var lat = Number(self.form.latitude) || -1.2921;
          var lon = Number(self.form.longitude) || 36.8219;
          var zoom = self.form.latitude ? 14 : 7;

          self.listingMapInstance = L.map('listingMap', { zoomControl: true, attributionControl: false }).setView([lat, lon], zoom);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18 }).addTo(self.listingMapInstance);

          // Click to drop/move pin
          self.listingMapInstance.on('click', function(e) {
            self.form.latitude = e.latlng.lat;
            self.form.longitude = e.latlng.lng;
            self.updateListingPin(e.latlng.lat, e.latlng.lng);
            self.reverseGeocode(e.latlng.lat, e.latlng.lng);
          });

          // If already has coords, show pin
          if (self.form.latitude && self.form.longitude) {
            self.updateListingPin(Number(self.form.latitude), Number(self.form.longitude));
          }
        });
      });
    },
    updateListingPin: function(lat, lon) {
      if (!this.listingMapInstance || !window.L) return;
      var pinIcon = L.divIcon({
        className: 'cl-pin-icon',
        html: '<div style="background:#BFA164;width:16px;height:16px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5);"></div>',
        iconSize: [16, 16], iconAnchor: [8, 8]
      });
      if (this.listingMarker) {
        this.listingMarker.setLatLng([lat, lon]);
      } else {
        this.listingMarker = L.marker([lat, lon], { icon: pinIcon, draggable: true })
            .addTo(this.listingMapInstance)
            .bindTooltip('Animal location', { permanent: false, direction: 'top' });
        var self = this;
        this.listingMarker.on('dragend', function(e) {
          var pos = e.target.getLatLng();
          self.form.latitude = pos.lat;
          self.form.longitude = pos.lng;
          self.reverseGeocode(pos.lat, pos.lng);
        });
      }
      this.listingMapInstance.setView([lat, lon], Math.max(this.listingMapInstance.getZoom(), 13));
    },

    // ============ NAVIGATION ============

    nextStep: function() {
      if (this.step < 5 && this.canAdvance) this.step++;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    prevStep: function() {
      if (this.step > 0) this.step--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    goToStep: function(s) {
      if (s <= this.step) this.step = s;
    },
    cancelListing: function() {
      if (this.form.title || this.photos.length > 0) {
        if (!confirm('Discard this listing? Your progress will be lost.')) return;
      }
      var animalId = this.$route.query.animalId || this.form.animalId;
      if (animalId) { this.$router.push('/farm/animal/' + animalId); return; }
      if (this.$route.query.batchId || this.$route.query.farmId) { this.$router.push('/farm'); return; }
      this.$router.push('/marketplace');
    },

    // ============ API: REFERENCE DATA ============

    async prefillFromAnimal(animalId) {
      try {
        var f = new FormData();
        f.append('function', 'getAnimalDetail');
        f.append('animalId', animalId);
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.animal) {
          var a = r.data.data.animal;
          this.form.animalId = a.id;
          this.form.farmId = a.farm_id || '';
          this.form.listingType = 'individual';
          this.form.speciesId = a.species_id || '';
          this.form.breedId = a.breed_id || '';
          this.form.sex = a.sex || '';
          this.form.weightKg = a.weight_kg || '';

          // Calculate age from DOB
          if (a.date_of_birth) {
            var dob = new Date(a.date_of_birth);
            var now = new Date();
            this.form.ageMonths = Math.floor((now - dob) / (30.44 * 86400000));
          } else if (a.estimated_age_months) {
            this.form.ageMonths = a.estimated_age_months;
          }

          // Build title
          var titleParts = [];
          if (a.breed_name) titleParts.push(a.breed_name);
          else if (a.species_name) titleParts.push(a.species_name);
          if (a.sex && a.sex !== 'unknown') titleParts.push(a.sex === 'male' ? 'Bull/Steer' : 'Cow/Heifer');
          if (a.name) titleParts.push('- ' + a.name);
          this.form.title = titleParts.join(' ');

          // Build description with farm records
          var desc = [];
          if (a.name) desc.push(a.name + ' — ' + (a.species_name || '') + (a.breed_name ? ' (' + a.breed_name + ')' : ''));
          if (a.weight_kg) desc.push('Current weight: ' + a.weight_kg + 'kg');
          if (a.health_status === 'healthy') desc.push('Health status: Healthy');
          if (a.color_markings) desc.push('Color/markings: ' + a.color_markings);

          // Add vaccination info
          var vax = r.data.data.animal.vaccinations || [];
          if (vax.length > 0) {
            desc.push('');
            desc.push('Vaccination records:');
            vax.forEach(function(v) {
              desc.push('• ' + v.vaccine_name + ' (' + v.disease_target + ') — ' + (v.administered_date || ''));
            });
          }

          // Add health records summary
          var health = r.data.data.animal.health_records || [];
          if (health.length > 0) {
            desc.push('');
            desc.push('Health history: ' + health.length + ' records on file');
          }

          // Add weight history
          var weights = r.data.data.animal.weight_history || [];
          if (weights.length > 1) {
            var first = weights[weights.length - 1];
            var last = weights[0];
            desc.push('Weight growth: ' + first.weight_kg + 'kg → ' + last.weight_kg + 'kg');
          }

          // Add breeding info
          if (a.reproductive_status && a.reproductive_status !== 'not_applicable') {
            desc.push('Reproductive status: ' + a.reproductive_status.replace(/_/g, ' '));
          }
          if (a.pedigree_number) desc.push('Pedigree #: ' + a.pedigree_number);

          desc.push('');
          desc.push('This animal has verified farm records on Flockr.');
          this.form.description = desc.join('\n');

          // Pre-fill location from farm
          if (a.farm_name) {
            // Try to get farm location
            try {
              var f2 = new FormData();
              f2.append('function', 'getFarmDetail');
              f2.append('farmId', a.farm_id);
              var r2 = await execute(f2, flocker);
              if (r2.data && r2.data.success && r2.data.data && r2.data.data.farm) {
                var farm = r2.data.data.farm;
                if (farm.county_id) this.form.countyId = farm.county_id;
                if (farm.latitude) this.form.latitude = farm.latitude;
                if (farm.longitude) this.form.longitude = farm.longitude;
                if (farm.county_name) this.form.locationDescription = farm.county_name;
              }
            } catch(e2) {}
          }

          // Load breeds for selected species
          if (this.form.speciesId) {
            this.fetchBreeds();
          }

          // Pre-load animal photos into the photos array for display (user can add more)
          var photos = r.data.data.animal.photos || [];
          if (photos.length > 0) {
            var self = this;
            photos.forEach(function(p) {
              if (p.photo_url) {
                self.photos.push({
                  preview: p.photo_url.startsWith('http') ? p.photo_url : (uploads + String(p.photo_url).replace('uploads/', '')),
                  file: null,
                  fromAnimal: true,
                  animalPhotoUrl: p.photo_url
                });
              }
            });
          }
        }
      } catch(e) {
        console.error('Failed to prefill from animal:', e);
      }
    },
    async prefillFromBatch(batchId, farmId) {
      var self = this;
      try {
        var r = await executeJson({function: 'getBatchDetail', batchId: batchId}, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.batch) {
          var b = r.data.data.batch;
          this.form.batchId = b.id;
          this.form.farmId = farmId || b.farm_id || '';
          this.form.listingType = 'batch';
          this.form.speciesId = b.species_id || '';
          this.form.breedId = b.breed_id || '';
          this.form.sex = b.sex || 'mixed';
          this.form.totalQuantity = b.available_quantity || b.total_quantity || '';
          this.form.availableQuantity = b.available_quantity || b.total_quantity || '';
          this.form.avgWeightKg = b.avg_weight_kg || '';

          // Build title
          var titleParts = [];
          if (b.breed_name) titleParts.push(b.breed_name);
          else if (b.species_name) titleParts.push(b.species_name);
          titleParts.push('Batch — ' + b.batch_name);
          if (b.available_quantity) titleParts.push('(' + b.available_quantity + ' available)');
          this.form.title = titleParts.join(' ');

          // Build description
          var desc = [];
          desc.push(b.batch_name + ' — ' + (b.species_name || '') + (b.breed_name ? ' (' + b.breed_name + ')' : ''));
          desc.push('Quantity: ' + (b.available_quantity || b.total_quantity) + ' animals');
          if (b.avg_weight_kg) desc.push('Average weight: ' + b.avg_weight_kg + 'kg');
          if (b.sex && b.sex !== 'mixed') desc.push('Sex: ' + b.sex);
          if (b.health_status) desc.push('Health status: ' + b.health_status);

          // Add vaccination info from batch detail
          var vax = r.data.data.vaccinations || [];
          if (vax.length > 0) {
            desc.push('');
            desc.push('Vaccination records:');
            vax.forEach(function(v) {
              desc.push('• ' + v.vaccine_name + ' (' + v.disease_target + ') — ' + (v.administered_date || ''));
            });
          }

          // Health summary
          var health = r.data.data.healthRecords || [];
          if (health.length > 0) {
            desc.push('');
            desc.push('Health history: ' + health.length + ' records on file');
          }

          // Cost info
          var costs = r.data.data.costs || {};
          if (costs.total > 0) {
            desc.push('');
            desc.push('Total investment: KES ' + Math.round(costs.total).toLocaleString());
          }

          desc.push('');
          desc.push('This batch has verified farm records on Flockr.');
          this.form.description = desc.join('\n');

          // Load breeds
          if (this.form.speciesId) this.fetchBreeds();

          // Pre-fill farm location
          if (this.form.farmId) {
            try {
              var f2 = new FormData();
              f2.append('function', 'getFarmDetail');
              f2.append('farmId', this.form.farmId);
              var r2 = await execute(f2, flocker);
              if (r2.data && r2.data.success && r2.data.data && r2.data.data.farm) {
                var farm = r2.data.data.farm;
                if (farm.county_id) this.form.countyId = farm.county_id;
                if (farm.latitude) this.form.latitude = farm.latitude;
                if (farm.longitude) this.form.longitude = farm.longitude;
                if (farm.county_name) this.form.locationDescription = farm.county_name;
              }
            } catch(e2) {}
          }

          // Load batch photos
          var batchPhotos = r.data.data.photos || [];
          if (batchPhotos.length > 0) {
            var uploads_base = typeof uploads !== 'undefined' ? uploads : '';
            batchPhotos.forEach(function(p) {
              if (p.photo_url) {
                self.photos.push({
                  preview: p.photo_url.startsWith('http') ? p.photo_url : (uploads_base + String(p.photo_url).replace('uploads/', '')),
                  file: null,
                  fromAnimal: true,
                  animalPhotoUrl: p.photo_url
                });
              }
            });
          } else if (b.primary_photo) {
            var uploads_base = typeof uploads !== 'undefined' ? uploads : '';
            self.photos.push({
              preview: b.primary_photo.startsWith('http') ? b.primary_photo : (uploads_base + String(b.primary_photo).replace('uploads/', '')),
              file: null,
              fromAnimal: true,
              animalPhotoUrl: b.primary_photo
            });
          }
        }
      } catch(e) {
        console.error('Failed to prefill from batch:', e);
      }
    },
    async fetchSpecies() {
      try {
        var response = await executeJson({ function: "getAnimalSpecies" }, flocker);
        if (response.data.success) this.speciesList = response.data.data.species || [];
      } catch (e) { console.error('Error fetching species:', e); }
    },

    // Dev-Requests #4 — load milk/eggs/hides/etc. categories lazily when the seller
    // switches to livestock_product mode.
    async fetchLivestockProductCategories() {
      if (this.livestockProductCategories.length) return;
      try {
        var r = await executeJson({ function: "getLivestockProductCategories" }, flocker);
        if (r.data.success) this.livestockProductCategories = r.data.data.categories || [];
      } catch (e) { console.error('Error fetching livestock product categories:', e); }
    },
    onKindChange() {
      if (this.form.listingKind === 'livestock_product') {
        this.fetchLivestockProductCategories();
      }
    },

    cutLineTotal(cut) {
      var w = Number(cut.weight_kg) || 0;
      var p = Number(cut.price_per_kg) || 0;
      return w > 0 && p > 0 ? Math.round(w * p * 100) / 100 : 0;
    },

    async fetchBreeds() {
      if (!this.form.speciesId) { this.breedsList = []; return; }
      try {
        var response = await executeJson({ function: "getAnimalBreeds", speciesId: this.form.speciesId }, flocker);
        if (response.data.success) this.breedsList = response.data.data.breeds || [];
      } catch (e) { console.error('Error fetching breeds:', e); }
    },

    async fetchCounties() {
      try {
        var response = await executeJson({ function: "getCounties", countryCode: "KE" }, flocker);
        if (response.data.success) this.counties = response.data.data.counties || [];
      } catch (e) { console.error('Error fetching counties:', e); }
    },

    async fetchSubCounties() {
      if (!this.form.countyId) { this.subCounties = []; return; }
      try {
        var response = await executeJson({ function: "getSubCounties", countyId: this.form.countyId }, flocker);
        if (response.data.success) this.subCounties = response.data.data.subCounties || [];
      } catch (e) { console.error('Error fetching sub-counties:', e); }
    },

    async fetchFarms() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) return;
      try {
        var response = await executeJson({ function: "getMyFarms", ownerId: userId }, flocker);
        if (response.data.success) {
          this.farms = response.data.data.farms || [];
          // Auto-pick the farm when a worker only belongs to one — no point making them choose from a list of one.
          if (this.isWorkerOnly && this.farms.length === 1 && !this.form.farmId) {
            this.form.farmId = this.farms[0].id;
            this.onFarmPicked();
          }
        }
      } catch (e) { console.error('Error fetching farms:', e); }
    },

    // When the user picks a farm, copy its saved location into the listing form
    // so they don't have to re-pin. Silently skips farms with no location saved
    // (UI surfaces an "add location to the farm" hint).
    onFarmPicked() {
      if (!this.form.farmId) return;
      var self = this;
      var farm = (this.farms || []).find(function(f) { return String(f.id) === String(self.form.farmId); });
      if (!farm) return;
      var lat = farm.latitude !== null && farm.latitude !== undefined && farm.latitude !== '' ? Number(farm.latitude) : null;
      var lon = farm.longitude !== null && farm.longitude !== undefined && farm.longitude !== '' ? Number(farm.longitude) : null;
      if (lat === null || lon === null || isNaN(lat) || isNaN(lon)) return;
      this.form.latitude = lat;
      this.form.longitude = lon;
      var desc = farm.address_details || farm.county_name || (lat + ', ' + lon);
      this.form.locationDescription = desc;
      this.locQuery = desc;
      // Update the map pin if the map is already mounted on this step
      this.$nextTick(function() {
        if (typeof self.updateListingPin === 'function') self.updateListingPin(lat, lon);
      });
    },

    onSpeciesChange: function() {
      this.form.breedId = '';
      this.fetchBreeds();
    },

    onCountyChange: function() {
      this.form.subCountyId = '';
      this.fetchSubCounties();
    },

    // ============ PHOTOS ============

    handlePhotoUpload: function(event) {
      var self = this;
      var files = Array.from(event.target.files || []);
      var remaining = 6 - self.photos.length;
      var toProcess = files.slice(0, remaining).filter(function(f) { return f.type.startsWith('image/'); });
      if (toProcess.length === 0) return;

      self.compressing = true;
      var processed = 0;

      toProcess.forEach(function(file) {
        self.compressImage(file, 1200, 0.8, function(compressedFile, previewUrl) {
          self.photos.push({ file: compressedFile, preview: previewUrl, originalSize: file.size });
          processed++;
          if (processed >= toProcess.length) self.compressing = false;
        });
      });

      event.target.value = '';
    },

    compressImage: function(file, maxDim, quality, callback) {
      // Target ~250 KB per photo. The upstream Craft Silicon proxy caps multipart bodies at ~1 MB,
      // so 6 photos * 250 KB stays comfortably inside. Iteratively lowers quality then dimension
      // until the output fits — never fails silently, always calls back with something.
      var TARGET_BYTES = 260 * 1024;
      var MIN_DIM = 640;
      var MIN_QUALITY = 0.35;
      var reader = new FileReader();
      reader.onload = function(e) {
        var img = new Image();
        img.onload = function() {
          var startDim = Math.min(maxDim || 1200, 1000);
          var startQuality = Math.min(quality || 0.8, 0.7);
          var fit = function(dim, q, done) {
            var w = img.width, h = img.height;
            if (w > dim || h > dim) {
              if (w > h) { h = Math.round(h * dim / w); w = dim; }
              else { w = Math.round(w * dim / h); h = dim; }
            }
            var canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            canvas.toBlob(function(blob) {
              if (!blob) { done(null); return; }
              if (blob.size <= TARGET_BYTES) { done(blob); return; }
              // Reduce quality first, then dimension
              if (q > MIN_QUALITY) { fit(dim, Math.max(MIN_QUALITY, q - 0.15), done); }
              else if (dim > MIN_DIM) { fit(Math.max(MIN_DIM, Math.round(dim * 0.85)), MIN_QUALITY, done); }
              else { done(blob); } // give up on target, use the smallest we could achieve
            }, 'image/jpeg', q);
          };
          fit(startDim, startQuality, function(blob) {
            if (!blob) { callback(file, e.target.result); return; }
            var compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
            callback(compressed, URL.createObjectURL(compressed));
          });
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    formatFileSize: function(bytes) {
      if (!bytes) return '0 KB';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    removePhoto: function(index) {
      this.photos.splice(index, 1);
    },

    // ============ SUBMIT ============

    async submitListing() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login'); return; }

      if (this.farmRequired && !this.form.farmId) {
        this.submitError = 'Please pick a farm — your listing will publish under the farm name, not your personal account.';
        this.step = 0;
        return;
      }

      this.submitting = true;
      this.submitError = '';

      try {
        var params = {
          function: "createListing",
          sellerId: userId,
          // Dev-Requests #4 — live animal vs livestock product
          listingKind: this.form.listingKind || 'live_animal',
          listingType: this.form.listingType,
          title: this.form.title,
          description: this.form.description,
          speciesId: this.form.listingKind === 'livestock_product' ? '' : this.form.speciesId,
          price: this.form.price,
          priceType: this.form.priceType,
          // Pilot-reviewer fix (issue #2): persist the unit on the listing row.
          priceUnit: this.form.priceUnit,
          currency: this.form.currency,
          countyId: this.form.countyId
        };
        if (this.form.listingKind === 'livestock_product' && this.form.productCategoryId) {
          params.productCategoryId = this.form.productCategoryId;
        }
        // Livestock products: fold harvest date into description so buyers see it
        // (no dedicated column on listings — smallest schema footprint).
        if (this.form.listingKind === 'livestock_product' && this.form.productHarvestDate) {
          const marker = this.isMeatCarcass ? 'Slaughtered: ' : 'Harvested / Prepared: ';
          if (!params.description.includes(marker)) {
            params.description = params.description + '\n' + marker + this.form.productHarvestDate;
          }
        }

        // Butchery workflow — ship the cuts breakdown; server persists to listing_cuts.
        if (this.isMeatCarcass && this.validCuts.length > 0) {
          params.cuts = JSON.stringify(this.validCuts.map(function(c) {
            return {
              cut_name: String(c.cut_name).trim(),
              weight_kg: Number(c.weight_kg),
              price_per_kg: Number(c.price_per_kg),
              quality_grade: c.quality_grade || ''
            };
          }));
        }

        // Per-kg pricing — ship the structured value alongside the headline price
        // so the server can persist both and other surfaces (search, detail, buy) can recompute.
        if (this.showPerKgCalc && this.form.pricePerKg) {
          params.pricePerKgLiveKes = this.form.pricePerKg;
          params.allowBuyLive = 1;
        }

        if (this.form.breedId) params.breedId = this.form.breedId;
        if (this.form.farmId) params.farmId = this.form.farmId;
        if (this.form.animalId) params.animalId = this.form.animalId;
        if (this.form.batchId) params.batchId = this.form.batchId;
        if (this.form.sex) params.sex = this.form.sex;
        if (this.form.ageMonths) params.ageMonths = this.form.ageMonths;
        if (this.form.weightKg) params.weightKg = this.form.weightKg;
        if (this.form.totalQuantity) params.totalQuantity = this.form.totalQuantity;
        if (this.form.availableQuantity) params.availableQuantity = this.form.availableQuantity;
        if (this.form.minOrderQuantity) params.minOrderQuantity = this.form.minOrderQuantity;
        if (this.form.avgWeightKg) params.avgWeightKg = this.form.avgWeightKg;
        if (this.form.locationDescription) params.locationDescription = this.form.locationDescription;
        if (this.form.latitude) params.latitude = this.form.latitude;
        if (this.form.longitude) params.longitude = this.form.longitude;
        if (this.form.offersDelivery) {
          params.offersDelivery = '1';
          params.deliveryBaseFee = this.form.deliveryBaseFee || '0';
          params.deliveryPerKm = this.form.deliveryPerKm || '0';
          params.deliveryMaxKm = this.form.deliveryMaxKm || '0';
          if (this.form.deliveryNotes) params.deliveryNotes = this.form.deliveryNotes;
        }

        // Build FormData for file uploads
        var formData = new FormData();
        Object.keys(params).forEach(function(key) {
          formData.append(key, params[key]);
        });
        var photoIdx = 0;
        var animalPhotoUrls = [];
        this.photos.forEach(function(ph) {
          if (ph.file) {
            var key = 'photo' + photoIdx;
            formData.append(key, ph.file);
            // Photo taken-at: prefer EXIF DateTimeOriginal (if pre-parsed), else file.lastModified.
            // file.lastModified is normally the photo's original capture time on phones.
            var takenAt = ph.takenAt || (ph.file && ph.file.lastModified ? new Date(ph.file.lastModified).toISOString().slice(0,19).replace('T',' ') : '');
            if (takenAt) formData.append('taken_at_' + key, takenAt);
            photoIdx++;
          } else if (ph.fromAnimal && ph.animalPhotoUrl) {
            animalPhotoUrls.push(ph.animalPhotoUrl);
          }
        });
        if (animalPhotoUrls.length > 0) {
          formData.append('animalPhotoUrls', JSON.stringify(animalPhotoUrls));
        }

        var response = await execute(formData, flocker);
        if (response.data.success) {
          var listingId = response.data.data.listingId;
          this.$router.push('/marketplace/listing/' + listingId);
        } else if (response.data.data && response.data.data.requireVerification) {
          // KYC gate from backend — route the seller to the verification page
          // with a hint banner so they know exactly why they were redirected.
          this.submitError = response.data.message || 'You need to verify your identity to list animals.';
          if (confirm(this.submitError + '\n\nOpen the Verification page now?')) {
            this.$router.push('/verification?from=create-listing');
          }
        } else if (response.data.data && response.data.data.matchListingId) {
          // Image fraud-guard rejection — link the seller straight to the matching listing
          // so they can see the duplicate and understand what we caught.
          this.submitError = response.data.message + ' (View: /marketplace/listing/' + response.data.data.matchListingId + ')';
        } else {
          this.submitError = response.data.message || 'Failed to create listing. Please try again.';
        }
      } catch (e) {
        console.error('Error creating listing:', e);
        var status = (e && e.response && e.response.status) || 0;
        if (status === 413) this.submitError = 'Photos too large. Try fewer photos or retake at lower resolution.';
        else if (status >= 500) this.submitError = 'Server error (' + status + '). Please try again in a moment.';
        else if (!navigator.onLine) this.submitError = 'You appear to be offline. Reconnect and try again.';
        else this.submitError = 'Upload failed' + (status ? ' (' + status + ')' : '') + '. Please try again.';
      } finally {
        this.submitting = false;
      }
    },

    // ============ HELPERS ============

    getSpeciesName: function() {
      var self = this;
      var sp = this.speciesList.find(function(s) { return String(s.id) === String(self.form.speciesId); });
      return sp ? sp.name : '';
    },
    getBreedName: function() {
      var self = this;
      var br = this.breedsList.find(function(b) { return String(b.id) === String(self.form.breedId); });
      return br ? br.name : '';
    },
    getCountyName: function() {
      var self = this;
      var c = this.counties.find(function(co) { return String(co.id) === String(self.form.countyId); });
      return c ? c.name : '';
    },
    formatPrice: function(amount) {
      if (!amount) return '0';
      return Number(amount).toLocaleString('en-KE');
    },
    fetchCurrencies: async function() {
      try {
        var r = await executeJson({ function: 'getCurrencies' }, flocker);
        if (r.data && r.data.success) this.currencies = (r.data.data && r.data.data.currencies) || [];
      } catch(e) { console.error(e); }
    },
    detectDefaultCurrency: async function() {
      // Pick the user's wallet currency first — falls back to phone-derived default on the
      // server side anyway, but pre-filling makes the picker show the right value before the
      // first interaction.
      try {
        var userId = sessionStorage.getItem('userId');
        if (!userId) return;
        var r = await executeJson({ function: 'getWalletBalance', userId: userId }, flocker);
        var ccy = r && r.data && r.data.data && r.data.data.wallet && r.data.data.wallet.currency;
        if (ccy) this.form.currency = ccy;
      } catch(e) { /* fall back to data() default */ }
    }
  }
};
</script>
